/**
 * Heading component using Tailwind CSS.
 * Migrated from styled-components (Dec 2024).
 */

import { Dynamic } from 'solid-js/web';
import clsx from 'clsx';
import { HeadingProps, HeadingVariant, HeadingTag } from './heading-types';
import { ErrorCatcher } from '../../tools/error-catcher';
import removeAmpersand from '../../tools/remove-ampersand';
import { resolveLegacyTextColor } from '../../style/theme';

/* Tag → default CVI size for non-manual headings. MUST stay identical to
   SizeTagMap in the WP block (services/wordpress/.../block/heading/heading-styles.js),
   which drives the editor preview — the two are separate copies across the
   headless boundary (WP editor can't import frontend code). Change both together. */
export const VariantTagMap = {
    'hero': 'heading-8xl',
    'heroEyebrow': 'heading-md',
    'h1': 'heading-4xl',
    'h2': 'heading-3xl',
    'h3': 'heading-2xl',
    'h4': 'heading-xl',
    'h5': 'heading-md',
    'span': 'heading-xl',
};

/**
 * Legacy Atos variant → CVI size. Flatten mapping: size only.
 * PDF variants are deliberately absent — they pass through untouched.
 */
const LEGACY_VARIANT_MAP: Partial<Record<HeadingVariant, HeadingVariant>> = {
    hero: 'heading-8xl',
    heroEyebrow: 'heading-md',
    xxlarge: 'heading-4xl',
    xlarge: 'heading-3xl',
    large: 'heading-2xl',
    medium: 'heading-xl',
    small: 'heading-md',
    bigGray: 'heading-xl',
    smallGray: 'heading-lg',
    tinyGray: 'heading-md',
    productNames: 'heading-lg',
    breadcrumb: 'heading-xl',
    giantRed: 'heading-3xl',
    bigRed: 'heading-3xl',
    smallRed: 'heading-lg',
    tinyRed: 'heading-md',
    temporarySmall: 'heading-md',
};

// Base classes for all headings (matches 'common' from heading.styles.tsx)
const baseClasses = [
    'heading-common',  // Uses @apply in tailwind.css
    '[&_a]:text-cvi-fg-link [&_a:hover]:underline',
];

/**
 * Variant class definitions
 *
 * Each variant uses component classes defined in tailwind.css (@layer components).
 * These classes reference CSS variables that handle responsive sizing via @media queries.
 */
/* Only CVI sizes (identity-mapped) and PDF variants live here. Legacy Atos
   names (xxlarge, giantRed, hero, …) are remapped to CVI by LEGACY_VARIANT_MAP
   in headingVariant() *before* this lookup. */
const variantClasses: Partial<Record<HeadingVariant, string>> = {
    'heading-md':  'heading-md',
    'heading-lg':  'heading-lg',
    'heading-xl':  'heading-xl',
    'heading-2xl': 'heading-2xl',
    'heading-3xl': 'heading-3xl',
    'heading-4xl': 'heading-4xl',
    'heading-5xl': 'heading-5xl',
    'heading-6xl': 'heading-6xl',
    'heading-7xl': 'heading-7xl',
    'heading-8xl': 'heading-8xl',

    // PDF variants — not remapped (LEGACY_VARIANT_MAP deliberately omits them).
    xxlargePDF: 'heading-xxlargePDF',
    xxlargeBoldPDF: 'heading-xxlargeBoldPDF',
    xlargePDF: 'heading-xlargePDF',
    xlargeBoldPDF: 'heading-xlargeBoldPDF',
    largePDF: 'heading-largePDF',
    mediumPDF: 'heading-mediumPDF',
    mediumBoldPDF: 'heading-mediumBoldPDF',
    smallPDF: 'heading-smallPDF',
    heroPDF: 'heading-heroPDF',
    heroEyebrowPDF: 'heading-heroEyebrowPDF',
    bigGrayPDF: 'heading-bigGrayPDF',
    smallGrayPDF: 'heading-smallGrayPDF',
    tinyGrayPDF: 'heading-tinyGrayPDF',
    giantRedPDF: 'heading-giantRedPDF',
    bigRedPDF: 'heading-bigRedPDF',
    smallRedPDF: 'heading-smallRedPDF',
    tinyRedPDF: 'heading-tinyRedPDF',
    productTitlePDF: 'heading-productTitlePDF',
};

interface HeadingComponent {
    (props: HeadingProps): any;
    parseProps?: (atts: any) => HeadingProps;
}

export const Heading: HeadingComponent = (props: HeadingProps) => {
    const headingVariant = () => {
        const raw = (props.variant ? props.variant : VariantTagMap[props.tag]) as HeadingVariant;
        return LEGACY_VARIANT_MAP[raw] ?? raw;
    };

    const headingClasses = () => clsx(
        baseClasses,
        variantClasses[headingVariant()],
        // Text alignment
        props.align === 'left' && 'text-left',
        props.align === 'center' && 'text-center',
        props.align === 'right' && 'text-right',
        // No block spacing
        props.noBlockSpacing && ['!mt-0', '!mb-0'],
        // Text transform (override variant default if specified)
        props.textTransform === 'uppercase' && 'uppercase',
        props.textTransform === 'lowercase' && 'lowercase',
        props.textTransform === 'capitalize' && 'capitalize',
        props.textTransform === 'none' && 'normal-case',
        // Capitalize first letter
        props.capitalizeFirstLetter && 'first-letter:uppercase',
        // Consumer-provided utility classes (escape hatch for color/weight overrides)
        props.class,
    );

    // Build inline styles for dynamic values
    const getStyles = () => {
        const styles: Record<string, string> = {};

        // Color from props (overrides variant color)
        if (props.color?.color) {
            styles.color = props.color.color;
        } else if (props.simpleColor) {
            styles.color = props.simpleColor;
        }

        // Line limit (clamping) - must be inline due to dynamic value
        if (props.lineLimit) {
            styles.overflow = 'hidden';
            styles.display = '-webkit-box';
            styles['-webkit-box-orient'] = 'vertical';
            styles['-webkit-line-clamp'] = String(props.lineLimit);
        }

        return styles;
    };

    return (
        <ErrorCatcher componentName='Heading'>
            <Dynamic
                component={props.tag as HeadingTag}
                innerHTML={removeAmpersand(props.children)}
                class={headingClasses()}
                style={getStyles()}
                onClick={props.onClick}
            />
        </ErrorCatcher>
    );
};

/**
 * Maps WordPress block attributes to Heading component props.
 * WordPress sends 'size' but the component expects 'variant'.
 */
Heading.parseProps = (atts: any): HeadingProps => {
    const resolvedHex = resolveLegacyTextColor(atts.color);
    return {
        tag: atts.tag,
        children: atts.children,
        variant: atts.manualSize ? atts.size : undefined, // Only use explicit size when manually set in WordPress
        color: resolvedHex ? { color: resolvedHex, name: atts.color?.name ?? 'fgDefault' } : undefined,
        align: atts.align,
        textTransform: atts.textTransform,
        noBlockSpacing: atts.noBlockSpacing,
        capitalizeFirstLetter: atts.capitalizeFirstLetter,
        lineLimit: atts.lineLimit,
    };
};

export default Heading;
